import { useState, useMemo, useReducer, useCallback } from "react";
import { Button } from "@carbon/react";
import i18next from "../../i18n";
import { formatDate, currencyFormatter } from "../../utils";
import { AwardOrderRequest, OffersToAccept, OrderResponse } from "../../types";
import OfferAssistantPanel from "../OfferAssistant/OfferAssistantPanel";
import { useAwardRecommendation } from "../OfferAssistant/useAwardRecommendation";
import AwardAcceptDialog from "../Award/AwardAcceptDialog";
import AwardOfferDetail from "../Award/AwardOfferDetail";
import AwardWarnings from "../Award/AwardWarnings";
import AwardJointSummary from "./AwardJointSummary";
import AwardMatrix from "../Award/AwardMatrix";
import { awardedSelection } from "../Award/awardFormat";
import {
  buildJointAwardPayload,
  formatJointOffers,
  getBestOffersTotal,
  getPriceByVolume,
} from "./awardJointFormat";
import {
  actions,
  createAwardJointReducer,
  initialEventState,
} from "./awardJointReducer";
import "../Award/award.scss";

type AwardJointProps = {
  offers: [OffersToAccept[]];
  onAcceptOffer: (offers: AwardOrderRequest[]) => void;
  onCancel: () => void;
  /** Retained for call-site compatibility; the panel is now closed by the parent's mutation
   * onSuccess rather than optimistically from here. */
  setIsAward?: (arg0: boolean) => void;
  order: OrderResponse[];
  /** The award mutation is in flight — keeps the confirm dialog open with a loading button. */
  isAwarding?: boolean;
  /** Shows a closed award as it was decided: no edits, no award, no offer assistant. */
  readOnly?: boolean;
};

const AwardJoint = ({
  offers,
  onAcceptOffer,
  onCancel,
  order,
  isAwarding = false,
  readOnly = false,
}: AwardJointProps) => {
  const [isBestSelected, setIsBestSelected] = useState(false);
  const [openAccept, setOpenAccept] = useState(false);
  const [offerSelectionComment, setOfferSelectionComment] = useState("");
  const [offerSelectionFileUrl, setOfferSelectionFileUrl] = useState("");
  const [selectedOfferDetails, setSelectedOfferDetails] = useState<any | null>(
    null
  );
  const [isSlideOpen, setIsSlideOpen] = useState(false);

  const sortedOrder = order?.sort((a, b) =>
    a?.material?.globalMaterialName?.localeCompare(
      b?.material?.globalMaterialName
    )
  );

  const formattedOffers = useMemo(() => formatJointOffers(offers), [offers]);

  const bestOffers = getBestOffersTotal(formattedOffers);

  const [event, updateEvent] = useReducer(
    createAwardJointReducer(order, formattedOffers),
    initialEventState,
    (initial) =>
      readOnly
        ? {
            ...initial,
            offersToAccept: awardedSelection(
              formattedOffers.flat().map((cell: any) => cell?.offer)
            ),
          }
        : initial
  );

  // Offer assistant: a recommendation pre-selects offers per material line (never awards) and
  // marks the matrix.
  const preselect = useCallback((offerIds: string[]) => {
    updateEvent({ type: actions.APPLY_RECOMMENDATION, offerIds });
    setIsBestSelected(false);
  }, []);
  const aiRecommendation = useAwardRecommendation(preselect);
  const multipleOrderId = order[0]?.multipleOrder?.id;

  const onAwardTender = () => {
    // Only fire the mutation; the parent closes this panel on success (so the dialog shows its
    // loading button meanwhile) and leaves it open on error to retry.
    const formatted = buildJointAwardPayload(
      event.offersToAccept,
      order,
      offerSelectionComment,
      offerSelectionFileUrl
    );
    onAcceptOffer(formatted);
  };

  const totalSelection = getPriceByVolume(event.offersToAccept);

  const selectedCurrencies = [
    ...new Set(
      event.offersToAccept.map((offer) => offer.offer?.currency).filter(Boolean)
    ),
  ];
  const hasMultipleCurrencies = selectedCurrencies.length > 1;

  // Add/remove one supplier's offer for one material line.
  const toggleOffer = (c: any, isSelected: boolean) => {
    if (isSelected) {
      updateEvent({ type: actions.DELETE_OFFER, id: c.offer.id });
    } else {
      updateEvent({
        type: actions.UPDATE_ON_CHECKBOX_CHANGE,
        id: c.offer.id,
        price: c.offer.price,
        volume: c.offer.order.quantity,
        orderId: c.offer.order.id,
        material: c.offer.order.material.globalMaterialName,
        offer: c.offer,
      });
    }
  };

  const awardedLines = event.offersToAccept.length;
  const canAward = awardedLines > 0 && event.invalid.length === 0;

  return (
    <>
      {!readOnly && (
        <AwardAcceptDialog
          open={openAccept}
          comment={offerSelectionComment}
          onCommentChange={setOfferSelectionComment}
          fileUrl={offerSelectionFileUrl}
          onFileUrlChange={setOfferSelectionFileUrl}
          onConfirm={onAwardTender}
          onCancel={() => setOpenAccept(false)}
          loading={isAwarding}
        />
      )}

      {!readOnly && multipleOrderId && (
        <OfferAssistantPanel
          multipleOrderId={multipleOrderId}
          {...aiRecommendation.panelProps}
        />
      )}

      <div style={{ marginTop: "1rem" }}>
        <AwardJointSummary
          isBestSelected={isBestSelected}
          onToggleBest={() => {
            setIsBestSelected(!isBestSelected);
            isBestSelected
              ? updateEvent({ type: actions.CLEAR_OFFERS })
              : updateEvent({ type: actions.SELECT_BEST_OFFERS });
          }}
          bestTotal={bestOffers.total}
          bestCoin={bestOffers.coin}
          selectionTotal={totalSelection.totalPrice}
          offersToAccept={event.offersToAccept}
          selectedCurrencies={selectedCurrencies as string[]}
          hasMultipleCurrencies={hasMultipleCurrencies}
          readOnly={readOnly}
        />

        <AwardMatrix
          materials={sortedOrder}
          supplierGroups={formattedOffers}
          offersToAccept={event.offersToAccept}
          errors={event.errors}
          aiMarks={readOnly ? null : aiRecommendation.marks}
          readOnly={readOnly}
          onSupplierToggle={(group) => {
            // Column header: award (or un-award) this supplier for every line at once.
            const isAllSelected = group
              .filter((c: any) => c?.offer)
              .every((c: any) =>
                event.offersToAccept.some((s: any) => s.id === c.offer.id)
              );
            if (isAllSelected) {
              group
                .filter((c: any) => c?.offer)
                .forEach((c: any) =>
                  updateEvent({ type: actions.DELETE_OFFER, id: c.offer.id })
                );
            } else {
              updateEvent({ type: actions.CLEAR_OFFERS });
              group
                .filter((c: any) => c?.offer)
                .forEach((c: any) =>
                  updateEvent({
                    type: actions.UPDATE_ON_CHECKBOX_CHANGE,
                    id: c.offer.id,
                    price: c.offer.price,
                    volume: c.offer.order.quantity,
                    orderId: c.offer.order.id,
                    material: c.offer.order.material.globalMaterialName,
                    offer: c.offer,
                  })
                );
            }
          }}
          onCellToggle={(c, isSelected) => toggleOffer(c, isSelected)}
          onVolumeChange={(c, volume) =>
            updateEvent({
              type: actions.UPDATE_VOLUME,
              id: c.offer.id,
              volume,
              orderId: c.offer.order.id,
            })
          }
          onPriceChange={(c, price) =>
            updateEvent({
              type: actions.UPDATE_PRICE,
              id: c.offer.id,
              price,
              orderId: c.offer.order.id,
            })
          }
          onViewDetail={(offer) => {
            setSelectedOfferDetails(offer);
            setIsSlideOpen(true);
          }}
        />
      </div>

      {!readOnly && (
        <AwardWarnings
          errors={event.errors}
          invalid={event.invalid}
          warningTitle={i18next.t("award.warning")}
        />
      )}

      <div className="award-bar">
        <div>
          <div className="award-bar__caption">
            {i18next.t(readOnly ? "award.awarded" : "award.yourSelection")} ·{" "}
            {i18next.t("award.linesAwarded", {
              count: awardedLines,
              total: sortedOrder?.length ?? 0,
            })}
          </div>
          <div className="award-bar__summary">
            {currencyFormatter(
              totalSelection.totalPrice,
              (selectedCurrencies[0] as string) ?? undefined
            )}
          </div>
        </div>
        <div className="award-bar__actions">
          {readOnly ? (
            <Button kind="secondary" onClick={onCancel}>
              {i18next.t("award.backToRfq")}
            </Button>
          ) : (
            <>
              <Button
                kind="secondary"
                onClick={() => {
                  updateEvent({
                    type: actions.CLEAR_OFFERS,
                    offersToAccept: [],
                  });
                  onCancel();
                }}
              >
                {i18next.t("cta.cancel")}
              </Button>
              <Button onClick={() => setOpenAccept(true)} disabled={!canAward}>
                {i18next.t("tenders.data.accept")}
              </Button>
            </>
          )}
        </div>
      </div>

      <AwardOfferDetail
        open={isSlideOpen}
        onClose={() => setIsSlideOpen(false)}
        offer={selectedOfferDetails}
        dateFormatter={formatDate}
      />
    </>
  );
};

export default AwardJoint;
